/*
  Mobile sticky buy bar for the product page.

  The bar holds no product state of its own. Its button submits the real form
  through the `form` attribute, and everything it displays is mirrored from the
  markup Dawn already maintains:

  - the price is copied from `#price-<section>`, the element product-info.js
    rewrites on every variant change;
  - the button's disabled state is copied from `#ProductSubmitButton-<section>`,
    which both product-info.js and product-form.js drive.

  Mirroring with a MutationObserver rather than subscribing to the variantChange
  event is deliberate: it also catches the paths that publish no event, such as
  an unavailable option combination, and the loading state during a submit.

  The bar hides itself while the real buy button is on screen, so the two never
  show at once. CSS shows the bar by default, so if this file never runs the
  visitor still gets a working button.
*/
(() => {
  const wire = (bar) => {
    if (bar.dataset.stickyAtcBound) return;
    bar.dataset.stickyAtcBound = 'true';

    const sectionId = bar.dataset.stickyAtc;
    const root = document.querySelector(`product-info[data-section="${sectionId}"]`) || document;

    const realButton = root.querySelector(`#ProductSubmitButton-${sectionId}`);
    const realPrice = root.querySelector(`#price-${sectionId}`);
    const button = bar.querySelector('[data-sticky-atc-button]');
    const price = bar.querySelector('[data-sticky-atc-price]');

    if (!realButton || !button) {
      /* No buy button to mirror - better no bar than a dead one. */
      bar.remove();
      return;
    }

    const addLabel = bar.dataset.addLabel;
    const soldOutLabel = bar.dataset.soldOutLabel;

    const syncButton = () => {
      const unavailable = realButton.hasAttribute('disabled');
      const busy = realButton.getAttribute('aria-disabled') === 'true';

      button.disabled = unavailable || busy;
      button.classList.toggle('is-busy', busy && !unavailable);
      button.textContent = unavailable ? soldOutLabel : addLabel;
    };

    const syncPrice = () => {
      if (!price || !realPrice) return;
      price.innerHTML = realPrice.innerHTML;
      price.hidden = realPrice.classList.contains('hidden');
    };

    syncButton();
    syncPrice();

    new MutationObserver(syncButton).observe(realButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'class'],
    });

    if (realPrice) {
      new MutationObserver(syncPrice).observe(realPrice, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => bar.classList.toggle('patc--tucked', entry.isIntersecting));
        },
        { rootMargin: '0px 0px -80px 0px' },
      ).observe(realButton);
    }
  };

  const init = () => document.querySelectorAll('[data-sticky-atc]').forEach(wire);

  init();

  /* The editor re-renders the whole section on a setting change, which drops
     the observers along with the old nodes. */
  document.addEventListener('shopify:section:load', init);
})();
